"use client"

import Image from "next/image"
import { motion } from "framer-motion"
import { Star, ShoppingBag, User } from "lucide-react"
import { Button } from "@/components/ui/button"

export default function Book() {
  const amazonLink = "https://www.amazon.com/Agentic-Artificial-Intelligence-Harnessing-Reinvent-ebook/dp/B0F1DS36YC?crid=2UGASTHXROK2Z&dib=eyJ2IjoiMSJ9.3WDOTy0Rc-_L7dTKxqAD0bzlKGCnMQMBJJSOVL6M6LmSJ38bYM-SLupKxTJn_QFBShQ0TCOI7_cOjh8vTXE3kg4A9mid_bcMFTtluWjYVIYMR1Kg8MuiApgJLZkFC0kXwA54VA5hgBCoBoWJVHdOTbC1a9s4v85GMDERalFECvM1jI8hancBAORRzc0r5o8MeHTOZDTonb91Tr3B_IJga_gr7sFIXoMoJS0EiMgSbZo.NmBv_VYMNr5K-y9VOUoY6EqbWM-ZGo7M1S_m2iSHX20&dib_tag=se&keywords=agentic+artificial+intelligence&qid=1745246570&sprefix=agentic+aritificial+intelligence%2Caps%2C118&sr=8-1&linkCode=ll1&tag=rakeshgohel0f-20&linkId=ba94fbea7c11eaca0e60cd007a6cb19e&language=en_US&ref_=as_li_ss_tl"

  return (
    <section id="book" className="relative py-12 bg-[#020617] overflow-hidden" itemScope itemType="https://schema.org/Book">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(91,192,235,0.05),transparent_50%)]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <h2 className="text-3xl md:text-4xl font-bold text-white font-[family-name:var(--font-plus-jakarta-sans)]">
              Book and Publications
            </h2>
            <div className="w-12 h-0.5 bg-blue-600" />
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* Left Column: Book Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center lg:items-start"
          >
            <div className="relative group w-full max-w-md mx-auto lg:mx-0 flex flex-col">
              {/* Background Depth Layer */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[96%] h-[94%] bg-[#163A98]/10 rounded-[24px] blur-sm transition-all duration-700 group-hover:bg-[#163A98]/20" />

              {/* Card Container: Gradient Border */}
              <div className="relative z-10 bg-[#163A98] from-white/[0.1] to-[#163A98]/30 p-[1px] rounded-[24px] overflow-hidden shadow-2xl">
                {/* Internal Content Area: Gradient Background + Glassmorphism */}
                <div className="bg-gradient-to-b from-[#02081A]/95 to-[#163A98]/40 backdrop-blur-2xl rounded-[23px] p-8">

                  <div className="relative w-full aspect-[4/5] transition-transform duration-700 group-hover:scale-[1.03] group-hover:-rotate-1">
                    <Image
                      src="/images/agentic_ai_book.webp"
                      alt="Agentic Artificial Intelligence Book"
                      fill
                      loading="lazy"
                      className="object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.6)]"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>


          {/* Right Column: Title and Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col justify-center"
          >
            <div className="mb-6">
              <h3 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white font-[family-name:var(--font-plus-jakarta-sans)] tracking-tight leading-tight mb-2" itemProp="name">
                Agentic
              </h3>
              <h4 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white/90 font-[family-name:var(--font-plus-jakarta-sans)] tracking-tight leading-tight mb-3">
                Artificial Intelligence
              </h4>
              <p className="text-lg md:text-xl text-tech-gray font-medium leading-tight max-w-lg" itemProp="description">
                Harnessing AI Agents to Reinvent Business, Work & Life
              </p>
            </div>

            <div className="flex items-center gap-4 mb-6">
              <div className="bg-[#E67E22] text-white px-3 py-1 rounded text-xs font-bold uppercase tracking-wider shadow-lg">
                #1 Best Seller
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-white">4.8</span>
                <div className="flex text-[#E67E22] gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} fill="currentColor" />
                  ))}
                </div>
              </div>
            </div>

            <div className="w-fit mb-8">
              <Button
                asChild
                className="h-14 px-6 rounded-full bg-white text-midnight-950 hover:bg-gray-100 text-lg font-bold shadow-lg group flex items-center gap-1"
              >
                <a href={amazonLink} target="_blank" rel="noopener noreferrer">
                  Buy from Amazon
                  <ShoppingBag className="text-black h-6 w-6 transition-transform group-hover:scale-110" />
                </a>
              </Button>
            </div>

            {/* Testimonial Card */}
            <div className="relative group max-w-lg">
              <div className="relative rounded-xl border border-[#1e293b] bg-[#0f172a]/50 p-5 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center border border-white/10 overflow-hidden">
                    <User className="text-white/70 w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h5 className="text-white font-bold text-base">Tarja Stephen</h5>
                      <div className="flex text-[#E67E22]">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} size={12} fill="currentColor" />
                        ))}
                      </div>
                      <span className="text-white/40 text-xs">Verified purchase</span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  "Agentic Artificial Intelligence" is a must-read for leaders and innovators who want more than AI buzzwords. It clearly explains how agentic systems, intelligent AI agents that act and collaborate and also how they are reshaping business...
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
